import { ref, onUnmounted, Ref } from 'vue';
import { useGSAPAnimations } from './useGSAPAnimations';
import { useGameAudioStore } from '@/stores/gameAudioStore';

interface Event {
  type: string;
  [key: string]: unknown;
}

interface AnimationEvent extends Event {
  target?: string | Element;
  props?: Record<string, unknown>;
  options?: Record<string, unknown>;
  object?: string | Element;
  duration?: number;
  element?: Element;
  index?: number;
  totalPieces?: number;
  elements?: Element[];
  positions?: Record<string, unknown>[];
  targets?: (string | Element)[];
  staggerOptions?: Record<string, unknown>;
  triggerOptions?: Record<string, unknown>;
  url?: string;
  fadeOutDuration?: number;
  func?: (...args: unknown[]) => unknown;
  params?: unknown[];
}

interface SpeechParams {
  text: string;
  rate?: number;
  pitch?: number;
  values?: string[];
}

interface EventHandlers {
  [key: string]: (event: Event, timelineId?: string) => void | Promise<void>;
}

interface AudioFunctions {
  playMusic: (
    url: string,
    options?: { loop?: boolean; volume?: number }
  ) => void;
  stopMusic: (fadeOutDuration?: number) => void;
  playEffect: (url: string) => void;
  playSpeech: (url: string) => void;
  stopSpeech: () => void;
  forceStopAllSpeech: () => void;
}

interface TimelineInstance {
  timeline: Record<number, Event>;
  currentTime: number;
  isPlaying: boolean;
  isPaused: boolean;
  timeoutId: number | null;
  id: string;
}

export function useTimeline() {
  function isValidTimeline(timeline: Record<string | number, Event>): boolean {
    return Object.keys(timeline).every((key) => {
      const numKey = Number(key);
      return !isNaN(numKey) && numKey !== 0;
    });
  }

  const {
    animate,
    spinAndFade,
    reverseSpinAndFade,
    animateBoardSwipeLeft,
    animateBoardSwipeRight,
    animateBoardSwipeUp,
    animateBoardSwipeDown,
    animateBoardSwipeIn,
    animatePuzzlePiece,
    animateAllPuzzlePieces,
    createScrollAnimation,
    staggerAnimation,
    revealText,
  } = useGSAPAnimations();

  let audioFunctions: AudioFunctions;
  try {
    const {
      playMusic,
      stopMusic,
      playEffect,
      playSpeech,
      stopSpeech,
      forceStopAllSpeech,
    } = useGameAudioStore();
    audioFunctions = {
      playMusic,
      stopMusic,
      playEffect,
      playSpeech,
      stopSpeech,
      forceStopAllSpeech,
    };
  } catch (error) {
    console.warn('Audio functions not available:', error);
    audioFunctions = {
      playMusic: () => {},
      stopMusic: () => {},
      playEffect: () => {},
      playSpeech: () => {},
      stopSpeech: () => {},
      forceStopAllSpeech: () => {},
    };
  }

  // Keep track of all active timeline instances
  const activeTimelines = new Map<string, TimelineInstance>();

  const tempSpeech = (event: SpeechParams, timelineId?: string): void => {
    const { text, rate = 1, pitch = 1, values = [] } = event;

    // Replace placeholders with values from array
    let processedText = text;
    const placeholders = text.match(/\{\{[^}]+\}\}/g) || [];

    placeholders.forEach((placeholder, index) => {
      if (values[index]) {
        processedText = processedText.replace(placeholder, values[index]);
      }
    });

    // Clean up the text
    const cleanText = processedText
      .replace(/<br\s*\/?>/gi, '. ')
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    const utterance = new SpeechSynthesisUtterance(cleanText);
    const synth = window.speechSynthesis;

    utterance.rate = Math.max(0.1, Math.min(10, rate));
    utterance.pitch = Math.max(0, Math.min(2, pitch));

    // Get audio store to manage speech state for volume ducking
    let gameAudio;
    try {
      gameAudio = useGameAudioStore();
    } catch (error) {
      console.warn('Audio store not available for tempSpeech:', error);
    }

    // Set speech playing state when speech starts
    utterance.onstart = () => {
      if (gameAudio) {
        gameAudio.setSpeechPlaying(true);
      }
    };

    // Clear speech playing state when speech ends
    utterance.onend = () => {
      if (gameAudio) {
        gameAudio.setSpeechPlaying(false);
      }
    };

    // Also handle error/abort cases
    utterance.onerror = () => {
      if (gameAudio) {
        gameAudio.setSpeechPlaying(false);
      }
    };

    utterance.onabort = () => {
      if (gameAudio) {
        gameAudio.setSpeechPlaying(false);
      }
    };

    const loadVoices = () => {
      const voices = synth.getVoices();
      const bestVoice =
        voices.find(
          (voice) =>
            voice.name.toLowerCase().includes('premium') ||
            voice.name.toLowerCase().includes('enhanced') ||
            voice.name.toLowerCase().includes('neural')
        ) || voices[0];

      if (bestVoice) {
        utterance.voice = bestVoice;
      }

      synth.cancel();
      synth.speak(utterance);
    };

    if (synth.getVoices().length > 0) {
      loadVoices();
    } else {
      speechSynthesis.addEventListener('voiceschanged', loadVoices, {
        once: true,
      });
    }
  };

  const eventHandlers: EventHandlers = {
    animate: (event: AnimationEvent) => {
      if (!event.target || !event.props) {
        console.warn('Missing target or props for animate event');
        return;
      }
      return animate(event.target, event.props, event.options);
    },
    spinAndFade: (event: AnimationEvent) => {
      if (!event.object || typeof event.duration !== 'number') return;
      return spinAndFade(event.object, event.duration);
    },
    reverseSpinAndFade: (event: AnimationEvent) => {
      if (!event.object || typeof event.duration !== 'number') return;
      return reverseSpinAndFade(event.object, event.duration);
    },
    swipeLeft: () => animateBoardSwipeLeft(),
    swipeRight: () => animateBoardSwipeRight(),
    swipeUp: () => animateBoardSwipeUp(),
    swipeDown: () => animateBoardSwipeDown(),
    swipeIn: () => animateBoardSwipeIn(),
    animatePuzzlePiece: (event: AnimationEvent) => {
      if (
        !event.element ||
        !event.options ||
        typeof event.index !== 'number' ||
        typeof event.totalPieces !== 'number'
      )
        return;
      return animatePuzzlePiece(
        event.element,
        event.options,
        event.index,
        event.totalPieces
      );
    },
    animateAllPuzzlePieces: (event: AnimationEvent) => {
      if (!event.elements || !event.positions) return;
      return animateAllPuzzlePieces(event.elements, event.positions);
    },
    scrollAnimation: (event: AnimationEvent) => {
      if (!event.target || !event.props) return;
      return createScrollAnimation(
        event.target,
        event.props,
        event.triggerOptions
      );
    },
    staggerAnimation: (event: AnimationEvent) => {
      if (!event.targets || !event.props) return;
      return staggerAnimation(event.targets, event.props, event.staggerOptions);
    },
    revealText: (event: AnimationEvent) => {
      if (!event.target || typeof event.duration !== 'number') return;
      return revealText(event.target, event.duration);
    },
    playMusic: (event: AnimationEvent) => {
      if (typeof event.url !== 'string') return;
      return audioFunctions.playMusic(event.url, event.options);
    },
    stopMusic: (event: AnimationEvent) => {
      if (event.fadeOutDuration && typeof event.fadeOutDuration !== 'number')
        return;
      return audioFunctions.stopMusic(event.fadeOutDuration);
    },
    playEffect: (event: AnimationEvent) => {
      if (typeof event.url !== 'string') return;
      return audioFunctions.playEffect(event.url);
    },
    playSpeech: (event: AnimationEvent, timelineId?: string) => {
      if (typeof event.url !== 'string') return;

      // Extra validation for speech events - make sure timeline is still active
      if (timelineId) {
        const instance = activeTimelines.get(timelineId);
        if (!instance || !instance.isPlaying) {
          console.log(
            `🎤 Skipping speech - timeline ${timelineId} was stopped before speech could play`
          );
          return;
        }
      }

      console.log(
        '🎤 Timeline playing speech:',
        event.url,
        timelineId ? `(timeline: ${timelineId})` : ''
      );
      return audioFunctions.playSpeech(event.url);
    },
    stopSpeech: () => audioFunctions.stopSpeech(),
    wait: (event: AnimationEvent) => {
      if (typeof event.duration !== 'number') return;
      return new Promise((resolve) =>
        setTimeout(resolve, event.duration * 1000)
      );
    },
    execute: (event: AnimationEvent) => {
      const { func, params } = event;
      if (typeof func !== 'function') return;
      return func(...(Array.isArray(params) ? params : []));
    },
    tempSpeech,
  };

  const setTimeline = (newTimeline: Record<number, Event>) => {
    if (!isValidTimeline(newTimeline)) {
      throw new Error('Invalid timeline: keys must be non-zero numbers');
    }

    // Check if this new timeline contains speech events
    const hasSpeechEvents = Object.values(newTimeline).some(
      (event) => event.type === 'playSpeech'
    );

    // If new timeline has speech events, stop ALL active timelines to prevent overlap
    if (hasSpeechEvents) {
      console.log(
        '🎤 New timeline contains speech - stopping ALL active timelines to prevent overlap'
      );

      // Stop all currently active timeline instances (this clears their setTimeout callbacks)
      const activeTimelineIds = Array.from(activeTimelines.keys());
      activeTimelineIds.forEach((id) => {
        console.log(`🎤 Stopping timeline ${id} to prevent speech overlap`);
        stopTimeline(id);
      });

      // Also stop any directly playing speech (non-timeline)
      audioFunctions.stopSpeech();
    }

    // Create a unique ID for this timeline instance
    const timelineId = Math.random().toString(36).substr(2, 9);

    const timelineEvents = Object.entries(newTimeline).reduce<
      Record<number, Event>
    >((acc, [time, event]) => {
      acc[Number(time)] = event;
      return acc;
    }, {});

    // Create new timeline instance
    const instance: TimelineInstance = {
      timeline: timelineEvents,
      currentTime: 0,
      isPlaying: false,
      isPaused: false,
      timeoutId: null,
      id: timelineId,
    };

    activeTimelines.set(timelineId, instance);

    const chainObj = {
      play() {
        playTimeline(timelineId);
        return chainObj;
      },
      pause() {
        pauseTimeline(timelineId);
        return chainObj;
      },
      stop() {
        stopTimeline(timelineId);
        return chainObj;
      },
      id: timelineId,
    };

    return chainObj;
  };

  const playTimelineAsync = async (
    newTimeline: Record<number, Event>
  ): Promise<void> => {
    if (!isValidTimeline(newTimeline)) {
      throw new Error('Invalid timeline: keys must be non-zero numbers');
    }
    const control = setTimeline(newTimeline);

    await new Promise<void>((resolve) => {
      setTimeout(resolve, 0); // Simulate asynchronous processing
    });
    control.play(); // Start the timeline immediately
    play(); // Start the timeline immediately
  };

  const playTimeline = (timelineId: string): void => {
    const instance = activeTimelines.get(timelineId);
    if (!instance) {
      console.error('Timeline not found:', timelineId);
      return;
    }

    if (instance.isPlaying && !instance.isPaused) {
      return;
    }

    instance.isPlaying = true;
    instance.isPaused = false;

    scheduleNextEvent(timelineId);
  };

  const pauseTimeline = (timelineId: string): void => {
    const instance = activeTimelines.get(timelineId);
    if (!instance) return;

    if (!instance.isPlaying || instance.isPaused) return;
    instance.isPaused = true;
    if (instance.timeoutId !== null) {
      clearTimeout(instance.timeoutId);
    }
  };

  const stopTimeline = (timelineId: string): void => {
    const instance = activeTimelines.get(timelineId);
    if (!instance) return;

    instance.isPlaying = false;
    instance.isPaused = false;
    instance.currentTime = 0;
    if (instance.timeoutId !== null) {
      clearTimeout(instance.timeoutId);
    }
    // Remove from active timelines
    activeTimelines.delete(timelineId);
  };

  const resetTimeline = (timelineId: string): void => {
    stopTimeline(timelineId);
  };

  // Legacy functions for backward compatibility
  const play = (): void => {
    // Legacy function - no-op
  };

  const pause = (): void => {
    // Legacy function - no-op
  };

  const stop = (): void => {
    // Legacy function - no-op
  };

  const reset = (): void => {
    // Legacy function - no-op
  };

  const scheduleNextEvent = (timelineId: string): void => {
    const instance = activeTimelines.get(timelineId);
    if (!instance) {
      console.error('Timeline instance not found:', timelineId);
      return;
    }

    if (!instance.isPlaying || instance.isPaused) return;

    const allTimes = Object.keys(instance.timeline).map(Number);
    const futureTimes = allTimes.filter((time) => time > instance.currentTime);
    const nextEventTime = futureTimes.sort((a, b) => a - b)[0];

    if (nextEventTime === undefined) {
      stopTimeline(timelineId);
      return;
    }

    const delay = (nextEventTime - instance.currentTime) * 1000;
    instance.timeoutId = setTimeout(async () => {
      // Check if this timeline is still active before executing
      const currentInstance = activeTimelines.get(timelineId);
      if (!currentInstance || !currentInstance.isPlaying) {
        console.log(`🎤 Skipping event - timeline ${timelineId} was stopped`);
        return;
      }

      instance.currentTime = nextEventTime;
      await executeEvent(instance.timeline[nextEventTime], timelineId);
      scheduleNextEvent(timelineId);
    }, delay) as unknown as number;
  };

  const executeEvent = async (
    event: Event,
    timelineId?: string
  ): Promise<void> => {
    // Double-check timeline validity for extra safety
    if (timelineId) {
      const instance = activeTimelines.get(timelineId);
      if (!instance || !instance.isPlaying) {
        console.log(
          `🎤 Skipping event execution - timeline ${timelineId} is no longer active`
        );
        return;
      }
    }

    const handler = eventHandlers[event.type];
    if (handler) {
      try {
        if (event.async) {
          await handler(event, timelineId);
        } else {
          handler(event, timelineId);
        }
      } catch (error) {
        console.error('Error executing handler:', error);
      }
    } else {
      console.warn(`Unknown event type: ${event.type}`);
    }
  };

  const addEventType = (
    type: string,
    handler: (event: Event, timelineId?: string) => void | Promise<void>
  ): void => {
    if (typeof handler !== 'function') {
      throw new Error('Event handler must be a function');
    }
    eventHandlers[type] = handler;
  };

  onUnmounted(() => {
    // Stop all active timelines on unmount
    for (const [timelineId] of activeTimelines) {
      stopTimeline(timelineId);
    }
    activeTimelines.clear();
  });

  return {
    setTimeline,
    playTimelineAsync,
    play,
    pause,
    stop,
    reset,
    addEventType,
    tempSpeech,
  };
}
